/**
 * What a player costs, on this league's budget.
 *
 * The dollars here are ESPN's published auction values, rebased. They are not
 * derived from our own projection any more: a derived price is an argument
 * about what a player is worth, and in a live auction the number you need is
 * what the room is going to make you pay. ESPN's board is the closest thing to
 * that which is actually publishable — Sleeper, despite running the draft,
 * exposes no auction value and no ADP anywhere in its API, so there is no
 * Sleeper number to show even though Sleeper is the room.
 *
 * Rebasing is not optional. ESPN quotes for ten teams at $200 — a $2,000 pot
 * across about 170 roster spots. A twelve-team $200 league is a $2,400 pot
 * across 180. Quoting their number unchanged in this league would underprice
 * every player on the board by about a fifth, which in an auction is not a
 * rounding error, it is the difference between winning your first nomination
 * and watching it go for eight dollars more than you bid.
 *
 * What the rebase cannot fix is coverage. ESPN prices 135 players and none of
 * them are kickers or defenses; this league rosters 180. The 45 spots ESPN has
 * no opinion on are the minimum bid by construction — which is very nearly
 * true, since they are the last men drafted — and that money is taken off the
 * table before anything else is priced, rather than being quietly spread
 * across the players ESPN does cover.
 */

/** Standard auction budget, used until a connected draft says otherwise. */
export const DEFAULT_BUDGET = 200;

/** Every rostered player costs at least this. */
export const MIN_BID = 1;

/**
 * ESPN's published prices, scaled onto this league's pot.
 *
 * The scale is the whole pot less a dollar for every roster spot ESPN does not
 * price, divided by what ESPN's own prices add up to. So the board's dollars
 * sum to what the room will actually spend, which is the property that makes
 * the inflation tracker's arithmetic mean anything: if the prices did not add
 * up to the pot, "money left over value left" would drift away from 1.0 on its
 * own and every reading of it would be measuring the scaling error rather than
 * the room.
 *
 * A player ESPN does not price gets no price rather than a zero. A zero is a
 * claim that he is worthless; what we have is no opinion, and the board should
 * say so.
 */
export const withAuctionValues = (rows, { budget, teams, rosterSize } = {}) => {
  const teamCount = Number(teams) > 0 ? Number(teams) : 12;
  const cap = Number(budget) > 0 ? Number(budget) : DEFAULT_BUDGET;
  const spots = Number(rosterSize) > 0 ? Number(rosterSize) : 15;

  const rostered = teamCount * spots;

  // Only the players who would actually be bought: ESPN prices a deeper board
  // than some leagues roster, and the ones past the last roster spot are not
  // competing for any of this money.
  const priced = rows
    .filter((row) => row.marketAuction > 0)
    .sort((a, b) => b.marketAuction - a.marketAuction)
    .slice(0, rostered);

  if (!priced.length) return rows.map((row) => ({ ...row, auction: null }));

  const theirs = priced.reduce((sum, row) => sum + row.marketAuction, 0);
  // Every roster spot ESPN has no price for still costs a dollar to fill, and
  // that dollar is spent before anyone bids on the players it does price.
  const unpriced = Math.max(0, rostered - priced.length);
  const pool = teamCount * cap - unpriced * MIN_BID;
  const scale = theirs > 0 && pool > 0 ? pool / theirs : 1;

  const byId = new Map(
    priced.map((row) => [row.id, Math.max(MIN_BID, Math.round(row.marketAuction * scale))]),
  );

  return rows.map((row) => ({
    ...row,
    auction: byId.get(row.id) ?? null,
    // The unscaled number, so the tooltip can name where the price came from.
    marketAuctionScaled: byId.get(row.id) ?? null,
  }));
};

export const formatAuctionTooltip = (row, { budget, teams } = {}) => {
  const cap = Number(budget) > 0 ? Number(budget) : DEFAULT_BUDGET;
  const teamCount = Number(teams) > 0 ? Number(teams) : 12;

  if (row?.auction == null) {
    return (
      'ESPN does not price him — no published value to rebase, so he is a ' +
      'dollar and a guess. They price no kickers or defenses at all.'
    );
  }

  return [
    `$${row.auction} of a $${cap} budget across ${teamCount} teams.`,
    `ESPN lists him at $${row.marketAuction} on their own ten-team, $200 board; ` +
      `this is that price scaled onto this league's pot.`,
  ].join('\n');
};
